import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDotComponent,
  OnyxInformationHeadingComponent,
  OnyxScheduleCompliance,
} from '@onyx/angular';
import { ModalLoadingSectionComponent } from '../../../../../../common/components/modals/modal-loading-section/modal-loading-section.component';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../../common/helpers/common.helper';
import { RouteHelper } from '../../../../../../common/helpers/route.helper';
import { TelematicsStore } from '../../../../../../common/stores/telematics.store';
import { OrderStatus } from '../../../../common/enums/order-status';
import { OrderHelper } from '../../../../common/helpers/order.helper';
import type { Order } from '../../../../common/interfaces/order';
import type { OrderPoint } from '../../../../common/interfaces/order-point';
import { OrderModalRealizationPointComponent } from './order-modal-realization-point/order-modal-realization-point.component';
import { OrderModalRealizationStatusComponent } from './order-modal-realization-status/order-modal-realization-status.component';

@Component({
  selector: 'app-order-modal-realization',
  imports: [
    OnyxInformationHeadingComponent,
    TranslatePipe,
    OnyxDotComponent,
    OrderModalRealizationPointComponent,
    OrderModalRealizationStatusComponent,
    ModalLoadingSectionComponent,
  ],
  templateUrl: './order-modal-realization.component.html',
  styleUrl: './order-modal-realization.component.scss',
})
export class OrderModalRealizationComponent {
  protected readonly commonHelper = inject(CommonHelper);
  private readonly telematicsStore = inject(TelematicsStore);

  public readonly order = input.required<Order>();
  public readonly currentPoint = input.required<OrderPoint>();

  protected isInProgress = computed(
    () => this.order().status.value === OrderStatus.IN_PROGRESS,
  );
  protected missingAssignment = computed(() =>
    OrderHelper.getMissingAssignment(this.order()),
  );
  protected showRelatedFleetSwapLoading = computed(
    () => this.order().relatedFleetSwap && this.telematicsStore.loading(),
  );
  protected currentSwapPoint = computed(() =>
    RouteHelper.getSwapCurrentRoutePoint(
      this.order().relatedFleetSwap,
      this.order().assignedData?.vehicle?.uuid,
      this.telematicsStore,
      this.order(),
    ),
  );
  protected pointData = computed(() =>
    RouteHelper.getPointRouteData(
      this.currentPoint(),
      this.order().points,
      this.commonHelper.now(),
    ),
  );

  protected heading = computed(() => {
    const missingAssignment = this.missingAssignment();
    if (missingAssignment && !this.order().relatedFleetSwap) {
      return missingAssignment.label;
    }

    const statusLabel = `${DictionaryCode.ORDER_STATUS}.${this.order().status.value}`;
    return (
      RouteHelper.getPointInProgressLabel(this.currentPoint(), this.order()) ??
      statusLabel
    );
  });
  protected color = computed(() => {
    if (this.missingAssignment() && !this.order().relatedFleetSwap) {
      return this.pointData()?.compliance === OnyxScheduleCompliance.DELAYED
        ? 'red'
        : 'gray';
    }

    if (
      this.isInProgress() &&
      !this.pointData()?.estimations &&
      !this.pointData()?.deadlineTime
    ) {
      return 'gray';
    }

    return this.pointData()?.complianceColor ?? this.order().status.color;
  });
}
